import React, { KeyboardEventHandler } from 'react';
import classNames from 'classnames';
import { Typography } from '../typography/typography';
import { Icon } from '../icon/icon';
import { useMaxViewport } from '../../core/hooks/use-max-viewport';
import styles from './stepper.module.scss';

type StepLabelProps = {
  label: string;
  active: boolean;
};

const StepLabel: React.FC<StepLabelProps> = ({ label, active }) => {
  return (
    <Typography className={classNames(styles.stepLabel, { [styles.active]: active })} variant="buttontext">
      {label}
    </Typography>
  );
};

type ConnectorProps = {
  active: boolean;
};

const Connector: React.FC<ConnectorProps> = ({ active }) => {
  return (
    <div className={classNames(styles.connectorRoot, { [styles.active]: active })}>
      <span className={classNames(styles.connectorLine, { [styles.active]: active })} />
    </div>
  );
};

type StepMeta = {
  label: string;
  onCompletedClick?: React.EventHandler<React.MouseEvent | React.KeyboardEvent>;
};

type StepProps = {
  stepNr: number;
  currentStep: number;
  index: number;
} & StepMeta;

const Step: React.FC<StepProps> = ({ index, stepNr, currentStep, label, onCompletedClick }) => {
  const completed = currentStep > stepNr;
  const active = currentStep === stepNr;
  const interactive = onCompletedClick && completed;
  const isTablet = useMaxViewport('s');

  const onKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === ' ' || e.key === 'Enter') {
      onCompletedClick?.(e);
    }
  };

  return (
    <div className={styles.stepContainer}>
      {index > 0 ? <Connector active={completed || active} /> : null}
      <div className={styles.step}>
        <div
          aria-label={label}
          role={interactive ? 'button' : undefined}
          tabIndex={interactive ? 0 : -1}
          className={classNames(styles.stepBlock, {
            [styles.active]: active,
            [styles.completed]: completed,
            [styles.interactive]: interactive,
          })}
          onClick={interactive ? onCompletedClick : undefined}
          onKeyDown={interactive ? onKeyDown : undefined}
        >
          {completed ? (
            <Icon className={styles.iconCompleted} size={isTablet ? '24' : '32'} variant="check" />
          ) : (
            <Typography variant="buttontext">{stepNr}</Typography>
          )}
        </div>
        <StepLabel active={active} label={label} />
      </div>
    </div>
  );
};

interface EnforceStepNumbers<T> extends Array<T> {
  length: 2 | 3 | 4;
}

type StepperProps = {
  currentStep: number;
  steps: EnforceStepNumbers<StepMeta>;
};

export const Stepper: React.FC<StepperProps> = ({ currentStep, steps }) => {
  return (
    <div className={styles.root}>
      {steps.map((step, index) => (
        <Step key={index} currentStep={currentStep} index={index} stepNr={index + 1} {...step} />
      ))}
    </div>
  );
};
